import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Prompt, useParams, useHistory, useLocation } from 'react-router-dom'
import { IconButton, makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import CodeIcon from '@material-ui/icons/Code'
import SydButton from '../../../../../commonDesign/Button'
import Icon from '../../../../../atoms/Icon'
import Loading from '../../../../../molecules/Loading'
import { smartEndpoints } from '../../../../../../api/smartEndpoints'
import AgentSidePanel from '../AgentSidePanel'
import InvocationCodesModal from '../InvocationCodesModal'
import { formatRelativeTime } from '../formatRelativeTime'
import SmartEndpointFormProvider, { useSmartEndpointForm } from './SmartEndpointFormContext'
import SmartEndpointWorkspaceProvider, { DEFAULT_ENDPOINT_CONFIG, useSmartEndpointWorkspace } from './SmartEndpointWorkspaceContext'
import ConfigPopover, { clampConfigValue } from './ConfigPopover'
import SmartEndpointErrorBoundary from './ErrorBoundary'
import SourceEditor from './SourceEditor'
import WorkSurface from './WorkSurface'

const RESERVED_POLICY_ID_SET = new Set(['allow', 'forbidden', 'smart_endpoint'])

function getReservedPolicyIds (policyIds = []) {
  return Array.from(new Set(
    (policyIds || []).filter(p => RESERVED_POLICY_ID_SET.has((p || '').trim()))
  ))
}

function normalizeEndpointConfig (config = {}) {
  return {
    maxReadRequests: clampConfigValue('maxReadRequests', config.maxReadRequests, DEFAULT_ENDPOINT_CONFIG.maxReadRequests),
    maxWriteRequests: clampConfigValue('maxWriteRequests', config.maxWriteRequests, DEFAULT_ENDPOINT_CONFIG.maxWriteRequests),
    timeoutMs: clampConfigValue('timeoutMs', config.timeoutMs, DEFAULT_ENDPOINT_CONFIG.timeoutMs),
    allowedPolicies: Array.isArray(config.allowedPolicies)
      ? Array.from(new Set(config.allowedPolicies.filter(p => typeof p === 'string')))
      : [...DEFAULT_ENDPOINT_CONFIG.allowedPolicies]
  }
}

function mapProgramTests (tests) {
  return (tests || []).map(t => {
    const c = t.testContents || {}
    return {
      name: c.name || 'Untitled test',
      testMode: c.testMode || 'unit',
      status: null,
      lastRunMs: null,
      input: c.input || '{}',
      responses: c.responses || '[]',
      assertions: c.assertions || '',
      lastResult: null
    }
  })
}

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldInput: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  versionSelect: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px'
  },
  modeToggle: {
    display: 'flex',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--color-neutral-300)'
  },
  modeBtn: {
    padding: '6px 12px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'var(--color-neutral-600)',
    transition: 'all var(--transition-duration-short)',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600)'
    }
  },
  modeBtnActiveWrite: {
    backgroundColor: 'var(--color-danger-500)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--color-danger-600)'
    }
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  codeLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px'
  },
  codesIconBtn: {
    padding: 2,
    color: 'var(--color-neutral-300)',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'transparent'
    }
  },
  tagBadgeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '1px'
  },
  tagBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'var(--font-mono, monospace)',
    letterSpacing: '0.2px',
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  tagBadgeLive: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  leftColumn: {
    flex: 1,
    minWidth: '380px'
  },
  middleColumn: {
    flex: 1,
    minWidth: '340px'
  },
  rightColumn: {
    width: '320px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  gearBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-700)'
    }
  },
  gearBtnOpen: {
    backgroundColor: 'var(--color-primary-050)',
    borderColor: 'var(--color-primary-300)',
    color: 'var(--color-primary-600)'
  },
  configPopoverAnchor: {
    position: 'relative'
  }
}))

const serializeSaveableTests = (tests) => JSON.stringify(
  (tests || []).map(t => ({ name: t.name, testMode: t.testMode, input: t.input, responses: t.responses, assertions: t.assertions }))
)

function SmartEndpointWorkspaceInner () {
  const classes = useStyles()
  const { smartEndpointId: codeParam } = useParams()
  const history = useHistory()
  const location = useLocation()
  const isEditing = !!codeParam

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const duplicateFrom = !isEditing ? searchParams.get('duplicateFrom') : null
  const duplicateName = !isEditing ? searchParams.get('name') : null

  const form = useSmartEndpointForm()
  const workspace = useSmartEndpointWorkspace()
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [savedState, setSavedState] = useState(null)
  const [codesModalOpen, setCodesModalOpen] = useState(false)
  const configRef = useRef(null)
  const hydrated = useRef(false)
  const isDirtyRef = useRef(false)
  const duplicateHydrated = useRef(false)

  const fetchCode = isEditing ? codeParam : duplicateFrom
  const { data: programData, isLoading } = smartEndpoints.useGetProgram(
    fetchCode || null,
    isEditing ? selectedVersion : null
  )
  const saveProgram = smartEndpoints.useSaveProgram()

  useEffect(() => {
    if (!duplicateFrom || !programData || duplicateHydrated.current) return
    const { program, tests } = programData
    form.setCode('')
    form.setDisplayName(duplicateName || `${program.displayName || ''} (copy)`)
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = mapProgramTests(tests)
    workspace.dispatchAction('loadTests', mapped)
    duplicateHydrated.current = true
  }, [duplicateFrom, duplicateName, programData, form, workspace])

  useEffect(() => {
    if (!isEditing) return
    if (!programData || hydrated.current) return
    const { program, tests } = programData
    form.setCode(program.code || '')
    form.setDisplayName(program.displayName || '')
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = mapProgramTests(tests)
    workspace.dispatchAction('loadTests', mapped)
    setSavedState({
      source: program.source || '',
      displayName: program.displayName || '',
      description: program.description || '',
      mode,
      config: JSON.stringify(normalizedConfig),
      modifiedDate: program.modifiedDate || null,
      tests: serializeSaveableTests(mapped)
    })
    setSelectedVersion(program.version)
    hydrated.current = true
  }, [isEditing, programData, form, workspace])

  useEffect(() => {
    if (selectedVersion && hydrated.current && programData?.program?.version !== selectedVersion) {
      hydrated.current = false
    }
  }, [selectedVersion, programData])

  const versions = useMemo(() => {
    if (!programData) return selectedVersion ? [selectedVersion] : [1]
    const maxV = programData.maxVersion || programData.program.version
    return Array.from({ length: maxV }, (_, i) => i + 1)
  }, [programData, selectedVersion])

  const currentVersion = selectedVersion || (programData?.program?.version) || 1

  const tagsForCurrentVersion = useMemo(() => {
    if (!programData?.tags) return []
    const matching = programData.tags
      .filter(t => t.version === currentVersion)
      .map(t => t.tagName)
    if (programData.maxVersion === currentVersion) matching.push('next')
    const order = { live: 0, next: 1 }
    return matching.sort((a, b) => (order[a] ?? 2) - (order[b] ?? 2))
  }, [programData, currentVersion])

  const handleSave = useCallback(async () => {
    if (!form.code || saving) return
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(form.code)) {
      setToast({ message: 'ID must be lowercase alphanumeric with hyphens', severity: 'error' })
      return
    }
    const reservedPolicies = getReservedPolicyIds(workspace.endpointConfig.allowedPolicies)
    if (reservedPolicies.length > 0) {
      setConfigOpen(true)
      setToast({
        message: `Remove reserved policies before saving: ${reservedPolicies.join(', ')}`,
        severity: 'error'
      })
      return
    }
    setSaving(true)
    try {
      const endpointConfig = normalizeEndpointConfig(workspace.endpointConfig)
      const tests = (workspace.testsRef?.current || []).map(t => ({
        name: t.name,
        testMode: t.testMode,
        input: t.input,
        responses: t.responses,
        assertions: t.assertions
      }))
      const result = await saveProgram.mutateAsync({
        programType: 'smart_endpoint',
        code: form.code,
        displayName: form.displayName,
        description: form.description,
        config: {
          ...endpointConfig,
          mode: workspace.mode
        },
        source: form.source,
        tests,
        modifiedDate: savedState?.modifiedDate || null
      })
      setSavedState({
        source: form.source,
        displayName: form.displayName,
        description: form.description,
        mode: workspace.mode,
        config: JSON.stringify(endpointConfig),
        modifiedDate: result.modifiedDate || null,
        tests: serializeSaveableTests(workspace.testsRef?.current)
      })
      setSelectedVersion(result.version)
      hydrated.current = false
      setToast({ message: result.created ? `Saved as v${result.version}` : 'Saved', severity: 'success' })
      if (!isEditing) {
        isDirtyRef.current = false
        history.replace(`/admin/denali/analysis/smart-endpoints/${form.code}`)
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save'
      const isConflict = err?.response?.status === 409
      setToast({
        message: isConflict ? 'Conflict: another user modified this endpoint. Please reload.' : msg,
        severity: 'error'
      })
    } finally {
      setSaving(false)
    }
  }, [form, workspace, saveProgram, saving, isEditing, history, savedState])

  const handleReset = useCallback(() => {
    if (!programData) {
      form.resetForm()
      workspace.setMode('query')
      workspace.setEndpointConfig(normalizeEndpointConfig(DEFAULT_ENDPOINT_CONFIG))
      workspace.dispatchAction('loadTests', [])
      return
    }
    const { program, tests } = programData
    form.setCode(program.code || '')
    form.setDisplayName(program.displayName || '')
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = mapProgramTests(tests)
    workspace.dispatchAction('loadTests', mapped)
    setSavedState({
      source: program.source || '',
      displayName: program.displayName || '',
      description: program.description || '',
      mode,
      config: JSON.stringify(normalizedConfig),
      modifiedDate: program.modifiedDate || null,
      tests: serializeSaveableTests(mapped)
    })
  }, [programData, form, workspace])

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && configOpen) {
        setConfigOpen(false)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        const agentPanel = document.querySelector('[data-agent-panel]')
        if (agentPanel && agentPanel.contains(document.activeElement)) return
        handleSave()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleSave, configOpen])

  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const workspaceContext = useMemo(() => ({
    source: form.source,
    testsRef: workspace.testsRef,
    endpointConfig: workspace.endpointConfig,
    code: form.code,
    mode: workspace.mode
  }), [form.source, workspace.testsRef, workspace.endpointConfig, form.code, workspace.mode])

  const handleApplySource = useCallback((src) => {
    form.setSource(src)
  }, [form])

  const handleApplyTest = useCallback((testArgs) => {
    workspace.dispatchAction('applyTest', testArgs)
  }, [workspace])

  const codeInvalid = form.code.length > 0 && !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(form.code)

  const currentTests = workspace.testsVersion >= 0
    ? serializeSaveableTests(workspace.testsRef?.current)
    : '[]'

  const isDirty = useMemo(() => {
    if (isEditing) {
      if (!savedState) return false
      return (
        form.source !== savedState.source ||
        form.displayName !== savedState.displayName ||
        form.description !== savedState.description ||
        workspace.mode !== savedState.mode ||
        JSON.stringify(workspace.endpointConfig) !== savedState.config ||
        currentTests !== savedState.tests
      )
    }

    const hasFormContent = form.hasSource || form.code.length > 0 || form.displayName.length > 0 || form.description.length > 0
    const hasNonDefaultMode = workspace.mode !== 'query'
    const hasNonDefaultConfig =
      workspace.endpointConfig.maxReadRequests !== DEFAULT_ENDPOINT_CONFIG.maxReadRequests ||
      workspace.endpointConfig.maxWriteRequests !== DEFAULT_ENDPOINT_CONFIG.maxWriteRequests ||
      workspace.endpointConfig.timeoutMs !== DEFAULT_ENDPOINT_CONFIG.timeoutMs ||
      JSON.stringify(workspace.endpointConfig.allowedPolicies) !== JSON.stringify(DEFAULT_ENDPOINT_CONFIG.allowedPolicies)
    const hasTests = currentTests !== '[]'

    return hasFormContent || hasNonDefaultMode || hasNonDefaultConfig || hasTests
  }, [isEditing, savedState, form, workspace.mode, workspace.endpointConfig, currentTests])

  useEffect(() => { isDirtyRef.current = isDirty }, [isDirty])

  const lastSavedInfo = useMemo(() => {
    if (!programData?.program) return null
    const { modifiedBy, modifiedDate, createdBy, createdDate } = programData.program
    const who = modifiedBy || createdBy || null
    const when = modifiedDate || createdDate || null
    if (!when) return null
    const ago = formatRelativeTime(when)
    return who ? `${who}, ${ago}` : ago
  }, [programData])

  if (isLoading && ((isEditing && !hydrated.current) || (duplicateFrom && !duplicateHydrated.current))) {
    return <Loading />
  }

  return (
    <div className={classes.page}>
      <Prompt
        when={isDirty}
        message={() => {
          if (!isDirtyRef.current) return true
          return 'You have unsaved changes. Are you sure you want to leave?'
        }}
      />
      <div className={classes.headerBar}>
        <div className={classes.headerField} style={{ width: '130px' }}>
          <div className={classes.codeLabelRow}>
            <label className={classes.fieldLabel} style={codeInvalid ? { color: 'var(--color-danger-500)' } : undefined}>ID</label>
            {isEditing && (
              <Tooltip title='Invocation codes' arrow>
                <IconButton
                  size='small'
                  className={classes.codesIconBtn}
                  onClick={() => setCodesModalOpen(true)}
                >
                  <CodeIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <input
            className={classes.fieldInput}
            value={form.code}
            onChange={(e) => form.setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder='endpoint-id'
            readOnly={isEditing}
            style={{
              ...(isEditing ? { backgroundColor: 'var(--color-neutral-050)', cursor: 'default' } : {}),
              ...(codeInvalid ? { borderColor: 'var(--color-danger-400)', boxShadow: '0 0 0 2px var(--color-danger-100)' } : {})
            }}
          />
        </div>
        <div className={classes.headerField} style={{ width: '200px' }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.fieldInput}
            value={form.displayName}
            onChange={(e) => form.setDisplayName(e.target.value)}
            placeholder='Untitled Smart Endpoint'
          />
        </div>
        <div className={classes.headerField} style={{ flex: 1 }}>
          <label className={classes.fieldLabel}>Description</label>
          <input
            className={classes.fieldInput}
            value={form.description}
            onChange={(e) => form.setDescription(e.target.value)}
            placeholder='Add a description...'
            style={{ width: '100%' }}
          />
        </div>
        <div className={classes.headerField}>
          <label className={classes.fieldLabel}>Version</label>
          <select
            className={classes.versionSelect}
            value={currentVersion}
            onChange={(e) => {
              const v = Number(e.target.value)
              setSelectedVersion(v)
              hydrated.current = false
            }}
          >
            {versions.map(v => (
              <option key={v} value={v}>v{v}</option>
            ))}
          </select>
        </div>

        <div className={classes.headerField}>
          {tagsForCurrentVersion.length > 0 && (
            <div className={classes.tagBadgeWrap}>
              {tagsForCurrentVersion.map(tag => (
                <Tooltip key={tag} title={tag === 'next' ? 'This is the latest version' : `Tag "${tag}" points to this version`} arrow>
                  <span className={`${classes.tagBadge} ${tag === 'live' ? classes.tagBadgeLive : ''}`}>
                    {tag}
                  </span>
                </Tooltip>
              ))}
            </div>
          )}
          <div className={classes.headerActions}>
            <div className={classes.modeToggle} role='group' aria-label='Endpoint mode'>
              <button
                className={`${classes.modeBtn} ${workspace.mode === 'query' ? classes.modeBtnActive : ''}`}
                onClick={() => workspace.setMode('query')}
                aria-pressed={workspace.mode === 'query'}
              >
                Read Only
              </button>
              <button
                className={`${classes.modeBtn} ${workspace.mode === 'command' ? classes.modeBtnActiveWrite : ''}`}
                onClick={() => workspace.setMode('command')}
                aria-pressed={workspace.mode === 'command'}
              >
                Read/Write
              </button>
            </div>
            <div className={classes.configPopoverAnchor} ref={configRef}>
              <button
                className={`${classes.gearBtn} ${configOpen ? classes.gearBtnOpen : ''}`}
                onClick={() => setConfigOpen(prev => !prev)}
                title='Endpoint configuration'
                aria-label='Endpoint configuration'
              >
                <Icon name='settings' customSize='16px' />
              </button>
              {configOpen && (
                <ConfigPopover
                  endpointConfig={workspace.endpointConfig}
                  onConfigChange={workspace.setEndpointConfig}
                  onToast={setToast}
                  onClose={() => setConfigOpen(false)}
                />
              )}
            </div>
            <SydButton size='sm' variant='secondary' onClick={handleReset} disabled={saving}>Reset</SydButton>
            <SydButton size='sm' variant='primary' icon='check' onClick={handleSave} disabled={!form.code || codeInvalid || saving}>
              {saving ? 'Saving\u2026' : 'Save'}
            </SydButton>
          </div>
        </div>
      </div>

      <div className={classes.body}>
        <div className={`${classes.column} ${classes.leftColumn}`}>
          <SourceEditor />
        </div>
        <div className={`${classes.column} ${classes.middleColumn}`}>
          <WorkSurface />
        </div>
        <div className={`${classes.column} ${classes.rightColumn}`}>
          <AgentSidePanel
            onApplySource={handleApplySource}
            onApplyTest={handleApplyTest}
            workspaceContext={workspaceContext}
          />
        </div>
      </div>

      <div className={classes.statusBar}>
        <span>
          <span
            className={classes.statusDot}
            style={{ backgroundColor: isDirty ? 'var(--color-warning-400)' : 'var(--color-success-500)' }}
          />
          {isDirty ? 'Unsaved changes' : 'Saved'}
        </span>
        <span>Version {currentVersion}{isDirty && savedState && form.source !== savedState.source ? ' (new source)' : ''}</span>
        <span>Mode: {workspace.mode === 'query' ? 'Read Only' : 'Read/Write'}</span>
        {lastSavedInfo && (
          <span>Last saved {lastSavedInfo}</span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          Smart Endpoint &middot; {form.code || 'no id'}
        </span>
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>

      {codesModalOpen && isEditing && (
        <InvocationCodesModal
          code={form.code}
          onClose={() => setCodesModalOpen(false)}
        />
      )}
    </div>
  )
}

function SmartEndpointWorkspace () {
  return (
    <SmartEndpointErrorBoundary>
      <SmartEndpointFormProvider>
        <SmartEndpointWorkspaceProvider>
          <SmartEndpointWorkspaceInner />
        </SmartEndpointWorkspaceProvider>
      </SmartEndpointFormProvider>
    </SmartEndpointErrorBoundary>
  )
}

export default SmartEndpointWorkspace
